import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import SendIcon from '@mui/icons-material/Send';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useUserContext } from '../hooks/useUserContext';
import emailService from '../services/emailService';

interface ContactFormState {
  fullName: string;
  email: string;
  topic: string;
  message: string;
}

const TOPICS = [
  'CPE Credits & Compliance Requirements',
  'Course Access & Learning Progress',
  'Certificates & Completion Records',
  'Billing & Subscription Management',
  'Sign-in, Profile, or Account Security',
  'Tenant Administration',
  'System Administration',
  'AI Assistant Suggestions or Issues',
  'Technical Bug Report',
  'Other',
];

const DEFAULT_FORM_STATE: ContactFormState = {
  fullName: '',
  email: '',
  topic: '',
  message: '',
};

const READ_ONLY_FIELD_SX = {
  '& .MuiInputBase-input[readonly]': {
    cursor: 'not-allowed',
    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'action.hover',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderStyle: 'dashed',
  },
};

export default function ContactUs() {
  const { clerkUser } = useUserContext();
  const [form, setForm] = useState<ContactFormState>(DEFAULT_FORM_STATE);
  const [submittedMessage, setSubmittedMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userFullName = useMemo(() => {
    const fullName = clerkUser?.fullName?.trim();
    if (fullName) {
      return fullName;
    }

    const firstName = clerkUser?.firstName?.trim() ?? '';
    const lastName = clerkUser?.lastName?.trim() ?? '';
    return [firstName, lastName].filter(Boolean).join(' ');
  }, [clerkUser]);

  const userEmail = useMemo(() => {
    return clerkUser?.primaryEmailAddress?.emailAddress ?? '';
  }, [clerkUser]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      fullName: userFullName,
      email: userEmail,
    }));
  }, [userEmail, userFullName]);

  const isValid = useMemo(() => {
    return Boolean(form.fullName.trim() && form.email.trim() && form.topic && form.message.trim());
  }, [form]);

  const handleFieldChange =
    (field: keyof ContactFormState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: string } }) => {
      const value = event.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isValid || isSubmitting) {
      return;
    }

    setSubmittedMessage(null);
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const payload = {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        topic: form.topic,
        message: form.message.trim(),
      };

      const result = await emailService.sendContactUsMessage(payload);

      if (result.success) {
        setSubmittedMessage(result.message);
        setForm((prev) => ({
          ...prev,
          topic: '',
          message: '',
        }));
      } else {
        setSubmitError(result.message);
      }
    } catch (error: unknown) {
      setSubmitError('Unable to submit your request right now. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ px: 3 }}>
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontSize: '32px',
            fontWeight: 400,
            lineHeight: 1.235,
            letterSpacing: '0.00735em',
            color: 'text.primary',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <ContactSupportIcon color="primary" sx={{ fontSize: 32 }} />
          Contact Us
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: '16px',
            lineHeight: 1.5,
            letterSpacing: '0.15px',
            color: 'text.secondary',
          }}
        >
          Reach our support team for help with CPE tracking, learning, billing, or account access.
        </Typography>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="stretch">
        <Card
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            flex: 1,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '20px',
                  fontWeight: 500,
                  lineHeight: 1.6,
                  letterSpacing: '0.15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <SupportAgentIcon color="primary" />
                Support Contact Details
              </Typography>

              <Stack spacing={1.5}>
                <Chip
                  icon={<PhoneIcon />}
                  label="Phone: (877) 370-2220"
                  variant="outlined"
                  sx={{ borderRadius: '100px', width: 'fit-content' }}
                />
                <Typography variant="body2" color="text.secondary">
                  Mon-Fri, 8:00 AM to 6:00 PM (Local Business Time)
                </Typography>
              </Stack>

              <Stack spacing={1.5}>
                <Chip
                  icon={<EmailIcon />}
                  label="Email: hello@cpacrossings.com"
                  variant="outlined"
                  sx={{ borderRadius: '100px', width: 'fit-content' }}
                />
                <Typography variant="body2" color="text.secondary">
                  For faster triage, include your tenant name, the affected page, and screenshots when possible.
                </Typography>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                You can also send an email directly to{' '}
                <Link href="mailto:hello@cpacrossings.com" underline="hover">
                  hello@cpacrossings.com
                </Link>{' '}
                or call{' '}
                <Link href="tel:+18773702220" underline="hover">
                  (877) 370-2220
                </Link>
                .
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            flex: 2,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '20px',
                  fontWeight: 500,
                  lineHeight: 1.6,
                  letterSpacing: '0.15px',
                }}
              >
                Send Us a Message
              </Typography>

              {submittedMessage && (
                <Alert severity="success" sx={{ borderRadius: '8px' }}>
                  {submittedMessage}
                </Alert>
              )}

              {submitError && (
                <Alert severity="error" sx={{ borderRadius: '8px' }}>
                  {submitError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      required
                      fullWidth
                      label="Full Name"
                      value={form.fullName}
                      InputProps={{ readOnly: true }}
                      helperText="Read-only from your profile"
                      sx={READ_ONLY_FIELD_SX}
                    />
                    <TextField
                      required
                      fullWidth
                      label="Email"
                      type="email"
                      value={form.email}
                      InputProps={{ readOnly: true }}
                      helperText="Read-only from your profile"
                      sx={READ_ONLY_FIELD_SX}
                    />
                  </Stack>

                  <FormControl fullWidth required>
                    <InputLabel id="contact-topic-label">Topic</InputLabel>
                    <Select
                      labelId="contact-topic-label"
                      value={form.topic}
                      label="Topic"
                      onChange={(event) => setForm((prev) => ({ ...prev, topic: event.target.value }))}
                    >
                      {TOPICS.map((topic) => (
                        <MenuItem key={topic} value={topic}>
                          {topic}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    required
                    fullWidth
                    multiline
                    minRows={4}
                    label="How can we help?"
                    value={form.message}
                    onChange={handleFieldChange('message')}
                    placeholder="Describe the issue, include dates/course names if relevant, and note any error messages you see."
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SendIcon />}
                      disabled={!isValid || isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
}
